<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  IconApps,
  IconBranch,
  IconBusiness,
  IconCalendar,
  IconCaretForward,
  IconChevronDownBox,
  IconList,
  IconNumber,
  IconRepeat,
  IconText,
  IconUser,
} from '@/components/design/icons';
import {
  TemplateFieldType,
  type FieldCatalog,
  type FieldCatalogEntity,
  type FieldCatalogField,
} from '@/api';
import {
  gaiaFontSize,
  gaiaTracking,
  gaiaFontWeight,
  gaiaFonts,
  gaiaNeutral,
  gaiaShadow,
  gaiaTemplating,
} from '@/components/design/themes/tokens';

interface Props {
  catalog: FieldCatalog | null;
  loading?: boolean;
  /**
   * Header subtitle. Defaults to the document-authoring copy. The snippet
   * library overrides it because there's no document to drag onto in that
   * view — only click-to-configure makes sense.
   */
  hint?: string;
  /**
   * Show the "Logic" section (conditionals + loops). Gated off by default so
   * it only appears in the snippet library, where these are click-to-build
   * reference snippets — the per-document authoring view doesn't use it.
   */
  showControlFlow?: boolean;
  /**
   * Whether tiles can be dragged. The Word add-in turns this off — Word's
   * document canvas can't receive HTML5 drops from a task pane, so there
   * click-to-insert is the only gesture.
   */
  draggable?: boolean;
}

interface Emits {
  (e: 'pick-primitive', type: TemplateFieldType): void;
  (e: 'pick-entity-field', entity: FieldCatalogEntity, field: FieldCatalogField): void;
  (e: 'pick-object-field'): void;
  (e: 'pick-conditional'): void;
  (e: 'pick-loop'): void;
}

const props = withDefaults(defineProps<Props>(), {
  hint: 'Drag a tile onto the document to insert it, or click to preview and copy the snippet.',
  showControlFlow: false,
  draggable: true,
});
defineEmits<Emits>();

const PRIMITIVE_LABELS: Record<TemplateFieldType, { label: string; icon: typeof IconBusiness }> = {
  string: { label: 'Text', icon: IconText },
  number: { label: 'Number', icon: IconNumber },
  date: { label: 'Date', icon: IconCalendar },
  // Boolean intentionally hidden from the palette — primitives are filtered
  // below — but kept here so the Record satisfies its TemplateFieldType keys.
  boolean: { label: 'Yes / No', icon: IconText },
  selection: { label: 'Selection', icon: IconChevronDownBox },
  object: { label: 'Object', icon: IconApps },
  list: { label: 'List', icon: IconList },
  legal_entity: { label: 'Legal entity', icon: IconBusiness },
  natural_person: { label: 'Natural person', icon: IconUser },
};

function entityIcon(entity: FieldCatalogEntity): typeof IconBusiness {
  return entity.entity_field_type === TemplateFieldType.NATURAL_PERSON ? IconUser : IconBusiness;
}

const primitives = computed(() =>
  (props.catalog?.primitive_types ?? []).filter(
    type =>
      type !== TemplateFieldType.LEGAL_ENTITY &&
      type !== TemplateFieldType.NATURAL_PERSON &&
      type !== TemplateFieldType.OBJECT &&
      type !== TemplateFieldType.LIST &&
      // Booleans aren't useful in the typical contract field set — drop the
      // tile to keep the palette focused on Text / Number / Date / Selection.
      type !== TemplateFieldType.BOOLEAN
  )
);

const entities = computed<FieldCatalogEntity[]>(() => props.catalog?.entities ?? []);

const expandedEntities = ref<Set<string>>(new Set());
function toggleEntity(name: string): void {
  const next = new Set(expandedEntities.value);
  if (next.has(name)) {
    next.delete(name);
  } else {
    next.add(name);
  }
  expandedEntities.value = next;
}

function startPrimitiveDrag(event: DragEvent, type: TemplateFieldType): void {
  if (!event.dataTransfer) return;
  event.dataTransfer.effectAllowed = 'copy';
  event.dataTransfer.setData(
    'application/x-template-field',
    JSON.stringify({ kind: 'primitive', type })
  );
}

function startEntityFieldDrag(
  event: DragEvent,
  entity: FieldCatalogEntity,
  field: FieldCatalogField
): void {
  if (!event.dataTransfer) return;
  event.dataTransfer.effectAllowed = 'copy';
  event.dataTransfer.setData(
    'application/x-template-field',
    JSON.stringify({
      kind: 'entity_field',
      entity_type: entity.name,
      entity_label: entity.label,
      sub_field: field.name,
      sub_field_type: field.type,
      required: field.required,
    })
  );
}

function startObjectFieldDrag(event: DragEvent): void {
  if (!event.dataTransfer) return;
  event.dataTransfer.effectAllowed = 'copy';
  event.dataTransfer.setData(
    'application/x-template-field',
    JSON.stringify({ kind: 'object_field' })
  );
}

function fieldTypeIcon(type: TemplateFieldType): typeof IconBusiness {
  return PRIMITIVE_LABELS[type].icon;
}

// --- chrome ------------------------------------------------------------------
const fsMeta = gaiaFontSize.meta;
const fwMedium = String(gaiaFontWeight.medium);
const fwSemibold = String(gaiaFontWeight.semibold);
const fwBold = String(gaiaFontWeight.bold);
const trackLabel = gaiaTracking.label;
const fsNano = gaiaFontSize.nano;
const fsRow = gaiaFontSize.row;
const surface = gaiaNeutral.surface;
const panelBorder = gaiaNeutral.borderDivider;
const tileBorder = gaiaNeutral.borderCard;
const tileHoverBorder = gaiaNeutral.borderHoverSoft;
const tileShadow = gaiaShadow.card;
const inkColor = gaiaNeutral.ink;
const subtleColor = gaiaNeutral.textSubtle;
const faintColor = gaiaNeutral.textFaint;
const hoverSurface = gaiaNeutral.surfaceSoft;
const codeSurface = gaiaNeutral.surfaceMuted;
const codeColor = gaiaNeutral.text;
const monoFont = gaiaFonts.mono;
const primitiveTile = gaiaNeutral.surfaceMuted;
const primitiveTileFg = gaiaNeutral.text;
const entityTile = gaiaTemplating.entitySurface;
const entityTileFg = gaiaTemplating.entityFg;
const entityGroupBorder = gaiaTemplating.entityBorder;
const personTile = gaiaTemplating.customSurface;
const personTileFg = gaiaTemplating.customFg;
const personGroupBorder = gaiaTemplating.customBorder;
const objectTile = gaiaTemplating.objectSurface;
const objectTileFg = gaiaTemplating.objectFg;
const logicTile = gaiaTemplating.blockSurface;
const logicTileFg = gaiaTemplating.blockFg;
const dashedDivider = gaiaNeutral.borderDashedSoft;
</script>

<template>
  <n-flex vertical class="palette" :size="18" :wrap="false">
    <header class="palette__header">
      <h2 class="palette__title">Fields</h2>
      <p class="palette__hint">{{ hint }}</p>
    </header>

    <div v-if="loading" class="palette__loading">Loading catalog…</div>

    <template v-else-if="catalog">
      <section v-if="primitives.length" class="palette__section">
        <n-h3 class="palette__section-title">Basic fields</n-h3>
        <n-flex vertical class="palette__grid" :size="6">
          <n-button
            v-for="type in primitives"
            :key="type"
            text
            class="palette-tile"
            :class="{ 'palette-tile--static': !draggable }"
            :draggable="draggable"
            @dragstart="startPrimitiveDrag($event, type)"
            @click="$emit('pick-primitive', type)"
          >
            <span class="palette-tile__icon palette-tile__icon--primitive">
              <n-icon :size="16">
                <component :is="PRIMITIVE_LABELS[type].icon" />
              </n-icon>
            </span>
            <span class="palette-tile__label">{{ PRIMITIVE_LABELS[type].label }}</span>
          </n-button>
        </n-flex>
      </section>

      <section v-if="entities.length" class="palette__section">
        <n-h3 class="palette__section-title">Entities</n-h3>
        <n-flex vertical class="palette__entities" :size="8">
          <div
            v-for="entity in entities"
            :key="entity.name"
            class="entity-group"
            :class="`entity-group--${entity.entity_field_type}`"
          >
            <button
              type="button"
              class="entity-group__header"
              :class="{ 'entity-group__header--expanded': expandedEntities.has(entity.name) }"
              @click="toggleEntity(entity.name)"
            >
              <span class="entity-group__chevron">
                <n-icon :size="12"><IconCaretForward /></n-icon>
              </span>
              <span class="entity-group__icon">
                <n-icon :size="14">
                  <component :is="entityIcon(entity)" />
                </n-icon>
              </span>
              <span class="entity-group__main">
                <span class="entity-group__label">{{ entity.label }}</span>
                <span v-if="entity.description" class="entity-group__sub">
                  {{ entity.description }}
                </span>
              </span>
            </button>

            <div v-show="expandedEntities.has(entity.name)" class="entity-group__fields">
              <n-button
                v-for="field in entity.fields"
                :key="field.name"
                text
                class="palette-tile palette-tile--sub"
                :class="{ 'palette-tile--static': !draggable }"
                :draggable="draggable"
                @dragstart="startEntityFieldDrag($event, entity, field)"
                @click="$emit('pick-entity-field', entity, field)"
              >
                <span class="palette-tile__icon palette-tile__icon--sub">
                  <n-icon :size="14">
                    <component :is="fieldTypeIcon(field.type)" />
                  </n-icon>
                </span>
                <span class="palette-tile__main">
                  <span class="palette-tile__label">{{ field.name }}</span>
                  <span class="palette-tile__sub">
                    {{ PRIMITIVE_LABELS[field.type]?.label ?? field.type }}
                  </span>
                </span>
              </n-button>
            </div>
          </div>
        </n-flex>
      </section>

      <section class="palette__section">
        <n-h3 class="palette__section-title">Custom objects</n-h3>
        <n-flex vertical class="palette__grid" :size="6">
          <n-button
            text
            class="palette-tile"
            :class="{ 'palette-tile--static': !draggable }"
            :draggable="draggable"
            @dragstart="startObjectFieldDrag($event)"
            @click="$emit('pick-object-field')"
          >
            <span class="palette-tile__icon palette-tile__icon--object">
              <n-icon :size="16">
                <IconApps />
              </n-icon>
            </span>
            <span class="palette-tile__main">
              <span class="palette-tile__label">Object field</span>
              <span class="palette-tile__sub">Custom <code>alias.field</code> reference</span>
            </span>
          </n-button>
        </n-flex>
      </section>

      <section v-if="showControlFlow" class="palette__section">
        <n-h3 class="palette__section-title">Logic</n-h3>
        <n-flex vertical class="palette__grid" :size="6">
          <n-button
            text
            class="palette-tile palette-tile--logic"
            @click="$emit('pick-conditional')"
          >
            <span class="palette-tile__icon palette-tile__icon--logic">
              <n-icon :size="16">
                <IconBranch />
              </n-icon>
            </span>
            <span class="palette-tile__main">
              <span class="palette-tile__label">Condition</span>
              <span class="palette-tile__sub">
                <code>if</code> / <code>elif</code> / <code>else</code>
              </span>
            </span>
          </n-button>
          <n-button text class="palette-tile palette-tile--logic" @click="$emit('pick-loop')">
            <span class="palette-tile__icon palette-tile__icon--logic">
              <n-icon :size="16">
                <IconRepeat />
              </n-icon>
            </span>
            <span class="palette-tile__main">
              <span class="palette-tile__label">Loop</span>
              <span class="palette-tile__sub"> <code>for</code> each item in a list </span>
            </span>
          </n-button>
        </n-flex>
      </section>

      <p class="palette__footer">
        <slot name="footer">
          For entity sub-fields, you'll be asked to pick a role (e.g. <code>seller</code>,
          <code>buyer</code>) so multiple drops can refer to the same person.
        </slot>
      </p>
    </template>
  </n-flex>
</template>

<style scoped>
/* Column layout/gap handled by NFlex; this keeps the panel chrome + scroll.
   Colours come from tokens — the tile families match the canvas chips: green
   for entity inserts, violet for people/custom, amber for logic blocks. */
.palette {
  padding: 18px 16px;
  background: v-bind(surface);
  border-right: 1px solid v-bind(panelBorder);
  height: 100%;
  overflow-y: auto;
}

.palette__header {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.palette__title {
  margin: 0;
  font-size: v-bind(fsRow);
  font-weight: v-bind(fwSemibold);
  color: v-bind(inkColor);
}

.palette__hint {
  margin: 0;
  font-size: v-bind(fsMeta);
  color: v-bind(subtleColor);
  line-height: 1.5;
}

.palette__loading {
  font-size: v-bind(fsRow);
  color: v-bind(subtleColor);
  text-align: center;
  padding: 24px 0;
}

.palette__section {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.palette__section-title {
  margin: 0;
  font-size: v-bind(fsNano);
  text-transform: uppercase;
  letter-spacing: v-bind(trackLabel);
  font-weight: v-bind(fwBold);
  color: v-bind(faintColor);
}

/* Rendered as an NButton (text variant). Its slot content lives in
   `.n-button__content`, so the row layout goes there; here we restore the
   tile's surface, full width, and auto height (NButton sizes a fixed height). */
.palette-tile {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 8px 10px;
  background: v-bind(surface);
  border: 1px solid v-bind(tileBorder);
  border-radius: 8px;
  text-align: left;
  cursor: grab;
  height: auto;
  transition: border-color 0.15s ease;
  width: 100%;
}

.palette-tile :deep(.n-button__content) {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-width: 0;
  white-space: normal;
}

.palette-tile:hover {
  border-color: v-bind(tileHoverBorder);
  background: v-bind(hoverSurface);
  box-shadow: v-bind(tileShadow);
}

.palette-tile:active {
  cursor: grabbing;
}

/* Non-draggable mode (Word add-in): tiles are plain click targets. */
.palette-tile--static,
.palette-tile--static:active {
  cursor: pointer;
}

.palette-tile--sub {
  padding: 6px 8px;
  border-color: transparent;
  background: transparent;
}

/* Logic tiles are click-to-build (not draggable) — show a pointer, not grab. */
.palette-tile--logic {
  cursor: pointer;
}

.palette-tile__icon--logic {
  background: v-bind(logicTile);
  color: v-bind(logicTileFg);
}

.palette-tile__sub code {
  background: v-bind(codeSurface);
  padding: 0 4px;
  border-radius: 4px;
  font-size: v-bind(fsNano);
  font-family: v-bind(monoFont);
  color: v-bind(codeColor);
}

.palette-tile__icon {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: v-bind(primitiveTile);
  color: v-bind(primitiveTileFg);
}

.palette-tile__icon--object {
  background: v-bind(objectTile);
  color: v-bind(objectTileFg);
}

.palette-tile__icon--sub {
  width: 20px;
  height: 20px;
  background: v-bind(primitiveTile);
  color: v-bind(subtleColor);
  border-radius: 5px;
}

.palette-tile__main {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  flex: 1 1 auto;
}

.palette-tile__label {
  font-size: v-bind(fsMeta);
  font-weight: v-bind(fwMedium);
  color: v-bind(inkColor);
  font-family: v-bind(monoFont);
}

.palette-tile__sub {
  font-size: v-bind(fsMeta);
  color: v-bind(subtleColor);
  line-height: 1.3;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.entity-group {
  border: 1px solid v-bind(tileBorder);
  border-radius: 9px;
  background: v-bind(surface);
  overflow: hidden;
}

.entity-group--legal_entity {
  border-color: v-bind(entityGroupBorder);
}

.entity-group--natural_person {
  border-color: v-bind(personGroupBorder);
}

.entity-group__header {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 10px;
  background: transparent;
  border: 0;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.entity-group__header:hover {
  background: v-bind(hoverSurface);
}

.entity-group__chevron {
  color: v-bind(faintColor);
  transition: transform 0.18s ease;
  display: inline-flex;
}

.entity-group__header--expanded .entity-group__chevron {
  transform: rotate(90deg);
}

.entity-group__icon {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  flex-shrink: 0;
}

.entity-group--legal_entity .entity-group__icon {
  background: v-bind(entityTile);
  color: v-bind(entityTileFg);
}

.entity-group--natural_person .entity-group__icon {
  background: v-bind(personTile);
  color: v-bind(personTileFg);
}

.entity-group__main {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.entity-group__label {
  font-size: v-bind(fsMeta);
  font-weight: v-bind(fwSemibold);
  color: v-bind(inkColor);
}

.entity-group__sub {
  font-size: v-bind(fsMeta);
  color: v-bind(subtleColor);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.entity-group__fields {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px 6px 6px;
  border-top: 1px solid v-bind(panelBorder);
}

.palette__footer {
  margin: auto 0 0;
  padding-top: 11px;
  border-top: 1px dashed v-bind(dashedDivider);
  font-size: v-bind(fsMeta);
  color: v-bind(faintColor);
  line-height: 1.55;
}

.palette__footer code {
  background: v-bind(codeSurface);
  padding: 1px 4px;
  border-radius: 4px;
  font-size: v-bind(fsNano);
  font-family: v-bind(monoFont);
  color: v-bind(codeColor);
}
</style>
